import {useNavigation, useRoute} from '@react-navigation/native';
import React, {useCallback, useEffect, useState} from 'react';
import {
  ActivityIndicator,
  Alert,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

import Header from '../../components/Header/Header';
import SearchHeader from '../../components/SearchHeader/SearchHeader';
import {Colors} from '../../constants/Colors';
import {NavigationProps} from '../../navigation/Routes';
import {AppRoutes} from '../../navigation/Routes.types';
import ScheduledProposalService, {
  AVistaOrderPreview,
  ScheduledOrderPreviewLine,
  ScheduledProposalUnavailableItem,
} from '../../services/ScheduledProposalService/ScheduledProposalService';
import {useUserStore} from '../../store/UserStore';
import {montarSacolaDaProposta} from '../../utils/seguirParaCheckout';
import {styles} from './styles';

const BRL = (valor: number): string =>
  (valor ?? 0).toLocaleString('pt-BR', {style: 'currency', currency: 'BRL'});

/**
 * O nome do produto é `descricao` + `complemento` — nunca só a descrição. É o
 * complemento que carrega embalagem e tamanho; sem ele, "Coca-Cola Sem Açúcar"
 * é o nome de todos os formatos do mesmo SKU. Mesma composição de `BagList`, do
 * catálogo e da revisão programada, com a mesma limpeza: o complemento vem do
 * SAP com o preço colado (`- R$`) e com `\n` literal.
 */
const nomeProduto = (
  descricao?: string | null,
  complemento?: string | null,
): string =>
  `${descricao ?? ''} ${(complemento ?? '')
    .replace('- R$', '')
    .replace(/\\n/g, '')}`
    .replace(/\s+/g, ' ')
    .trim();

/** `2026-08-20` -> `20/08/2026`. Sem `new Date`: o parse de data pura vira UTC
 * e volta um dia no fuso do Brasil. */
const dataBR = (iso?: string | null): string => {
  if (!iso) {
    return '';
  }
  const [ano, mes, dia] = iso.split('-');
  return dia && mes && ano ? `${dia}/${mes}/${ano}` : iso;
};

/*
 * Componentes de apresentação em escopo de MÓDULO, e não dentro da tela.
 * Definidos lá dentro, o React vê um tipo novo a cada render e destrói a
 * subárvore inteira — a lista toda remontando a cada toque.
 */

const Linha = ({linha}: {linha: ScheduledOrderPreviewLine}) => {
  const removido = linha.acao === 'REMOVIDO';
  const reduzido = linha.acao === 'REDUZIDO';

  return (
    <View style={[styles.linha, removido && styles.linhaRemovida]}>
      <Text
        style={[styles.linhaTitulo, removido && styles.textoRiscado]}
        numberOfLines={2}>
        {nomeProduto(linha.descricao, linha.produto?.complemento) || linha.sku}
      </Text>

      {removido ? (
        <Text style={styles.linhaRemovidaTexto}>
          Sem estoque — item removido do pedido
        </Text>
      ) : (
        <>
          <View style={styles.linhaValores}>
            <Text style={styles.linhaTexto}>
              {reduzido ? (
                <>
                  <Text style={styles.textoRiscado}>
                    {linha.quantidadeProgramada}
                  </Text>
                  {'  '}
                  <Text style={styles.linhaDestaque}>
                    {linha.quantidadeAjustada}
                  </Text>
                </>
              ) : (
                `${linha.quantidadeAjustada}`
              )}
              {' un. × '}
              {BRL(linha.precoAjustado)}
            </Text>
            <Text style={styles.linhaSubtotal}>
              {BRL(linha.subtotalAjustado)}
            </Text>
          </View>

          {reduzido ? (
            <Text style={styles.linhaAviso}>
              Estoque insuficiente: quantidade reduzida de{' '}
              {linha.quantidadeProgramada} para {linha.quantidadeAjustada}.
            </Text>
          ) : null}

          {/* Menos produto, preço unitário MAIOR: a redução cruzou um degrau da
              escala por quantidade. Sem esta linha o cliente só descobriria
              pelo total — e aqui ele vai pagar na hora. */}
          {linha.precoSubiu ? (
            <Text style={styles.linhaAvisoPreco}>
              O preço unitário subiu de {BRL(linha.precoProgramado)} para{' '}
              {BRL(linha.precoAjustado)} porque a quantidade saiu da faixa de
              volume.
            </Text>
          ) : null}
        </>
      )}
    </View>
  );
};

/**
 * PAG-01 R-4 — o que FICOU DE FORA DA COBRANÇA.
 *
 * Este bloco é a contrapartida da decisão de cobrar o resto em vez de recusar o
 * pedido inteiro. O cliente **paga vendo**: descobrir depois do PIX que dois
 * itens não vieram é o pior momento possível, porque o dinheiro já saiu e a
 * conversa vira reclamação em vez de escolha.
 */
const BlocoIndisponiveis = ({
  itens,
}: {
  itens: ScheduledProposalUnavailableItem[];
}) => (
  <View style={styles.blocoIndisponiveis}>
    <Text style={styles.blocoIndisponiveisTitulo}>
      Itens que não entram nesta cobrança
    </Text>
    {itens.map((item, indice) => (
      <Text
        key={`${item.sku}-${indice}`}
        style={styles.blocoIndisponiveisTexto}>
        {nomeProduto(item.description, item.complemento) || item.sku}
        {' — '}
        {item.reason === 'SEM_PRECO'
          ? 'sem preço para a sua loja'
          : item.reason === 'FORA_DO_CATALOGO'
          ? 'indisponível para a sua loja'
          : 'sem estoque'}
      </Text>
    ))}
    <Text style={styles.blocoIndisponiveisTexto}>
      Fale com seu vendedor sobre estes itens. O restante segue normalmente.
    </Text>
  </View>
);

/**
 * PAG-01 C.5 — a tela do PEDIDO A PAGAR.
 *
 * ## O que ela NÃO tem, e por quê
 *
 * **Não tem aceite.** Pagar é aceitar. A venda programada tem um ato de aceite
 * próprio (`ScheduledSetAcceptController`) porque o pedido dela nasce dias
 * depois, no crédito do cliente, e alguém precisa dizer sim antes. Aqui o
 * vendedor acabou de fechar a venda no PDV e o dinheiro sai agora: um passo de
 * "aceitar" antes de "pagar" seria confirmar duas vezes a mesma coisa.
 *
 * **Não tem bonificação nem data real de entrega.** Esta rodada é só VENDA, e a
 * entrega segue a rota normal do dia da visita.
 *
 * **Não tem janela.** O prazo é 23:59 de hoje — o mesmo que o vendedor
 * anunciou no PDV —, e é a informação que a tela mais precisa dar.
 *
 * ## Tela própria, e não um ramo da revisão programada
 *
 * Aquela tela existe para *n* vendas em blocos, com bonificações, com
 * `jaPedida` e com o aceite em bloco. Metade dos conceitos dela não se aplica
 * aqui, e escondê-los com condicionais deixaria as duas telas presas uma à
 * outra — a próxima mudança na programada teria de ser pensada duas vezes.
 *
 * O que é compartilhado de verdade é compartilhado de verdade: a folha de
 * estilo (`./styles`) e a montagem da sacola (`montarSacolaDaProposta`).
 */
const AVistaOrderReview = () => {
  const {navigate, goBack} = useNavigation<NavigationProps>();
  const route = useRoute<any>();
  const setId: string = route.params?.setId ?? '';

  const {user} = useUserStore();
  const clientCode: string =
    route.params?.clientCode ||
    user?.current_client?.clientCode ||
    user?.clientCode ||
    '';

  const [preview, setPreview] = useState<AVistaOrderPreview | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [erro, setErro] = useState<boolean>(false);

  const carregar = useCallback(async () => {
    if (!setId || !clientCode) {
      setLoading(false);
      setErro(true);
      return;
    }
    setLoading(true);
    setErro(false);
    try {
      setPreview(
        await ScheduledProposalService.GetAVistaOrderPreview(clientCode, setId),
      );
    } catch (error) {
      console.error('Erro ao carregar o pedido a pagar:', error);
      setErro(true);
    } finally {
      setLoading(false);
    }
  }, [setId, clientCode]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  /**
   * Monta a sacola e segue para o checkout de sempre — o mesmo do pedido
   * comum, com uma diferença: `A_VISTA` faz o checkout oferecer PIX e cartão,
   * sem boleto.
   *
   * O boleto não é escondido por gosto. O backend o RECUSA com 422 para o à
   * vista: ele marca o pedido como enviado na confirmação do PEDIDO, não do
   * pagamento, e faria o Conecta Sales anunciar ao vendedor um pagamento que
   * não aconteceu.
   */
  const pagar = () => {
    if (!preview) {
      return;
    }

    if (!preview.pagavel) {
      // Segunda porta: o botão já está escondido nesses casos, mas um toque
      // pode chegar antes do próximo render.
      Alert.alert(
        'Pedido indisponível',
        'O prazo para pagar este pedido terminou. Fale com seu vendedor.',
      );
      return;
    }

    const montou = montarSacolaDaProposta(
      preview.linhas,
      preview.orderApplicationId,
      preview.referenceDate,
      'A_VISTA',
    );

    if (!montou) {
      return;
    }

    navigate(AppRoutes.CHECKOUT);
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <Header justDropDown={false} />
        <ActivityIndicator
          style={styles.spinner}
          size="large"
          color={Colors.RedPallete.red}
        />
      </View>
    );
  }

  if (erro || !preview) {
    return (
      <View style={styles.container}>
        <Header justDropDown={false} />
        <View style={styles.estadoVazio}>
          <Text style={styles.estadoVazioTitulo}>
            Não conseguimos carregar este pedido
          </Text>
          <TouchableOpacity onPress={carregar}>
            <Text style={styles.tentarNovamente}>Tentar novamente</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={goBack}>
            <Text style={styles.voltar}>Voltar</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  const bloqueado = preview.bloqueioFinanceiro !== null;
  const podePagar = preview.pagavel && !preview.semItens && !bloqueado;

  return (
    <View style={styles.container}>
      <Header justDropDown={false} />

      <ScrollView contentContainerStyle={styles.conteudo}>
        <SearchHeader onPress={goBack} title="Pedido a pagar" mb={4} />
        <Text style={styles.subtituloTela}>
          Montado pelo seu vendedor em {dataBR(preview.referenceDate)}
        </Text>

        {/* O PRAZO é a informação que distingue esta tela da revisão
            programada, e é por isso que ela vem antes de tudo. O cliente
            precisa saber que isto vence hoje antes de decidir "vejo depois". */}
        <View
          style={[styles.prazo, !preview.pagavel && styles.prazoEncerrado]}>
          <Text
            style={[
              styles.prazoTitulo,
              !preview.pagavel && styles.prazoTituloEncerrado,
            ]}>
            {preview.pagavel
              ? 'Pague hoje até 23:59'
              : 'O prazo deste pedido terminou'}
          </Text>
          <Text
            style={[
              styles.prazoTexto,
              !preview.pagavel && styles.prazoTextoEncerrado,
            ]}>
            {preview.pagavel
              ? 'Depois deste horário o pedido é cancelado automaticamente e seu vendedor é avisado.'
              : 'Fale com seu vendedor para montar um novo pedido.'}
          </Text>
        </View>

        {/* O cliente precisa saber que não é uma sacola comum ANTES de procurar
            o botão de editar que não existe. */}
        <View style={styles.avisoSomenteLeitura}>
          <Text style={styles.avisoSomenteLeituraTexto}>
            Este pedido foi montado pelo seu vendedor e não pode ser editado.
            Você paga o valor abaixo pelo app.
          </Text>
        </View>

        {preview.houveAjuste ? (
          <View style={styles.avisoAjuste}>
            <Text style={styles.avisoAjusteTitulo}>
              Ajustamos seu pedido por disponibilidade de estoque
            </Text>
            <Text style={styles.avisoAjusteTexto}>
              Confira as quantidades{preview.precoMudou ? ' e os preços' : ''}{' '}
              antes de pagar.
            </Text>
          </View>
        ) : null}

        {preview.linhas.map((linha, indice) => (
          <Linha key={`${linha.sku}-${indice}`} linha={linha} />
        ))}

        {preview.indisponiveis.length ? (
          <BlocoIndisponiveis itens={preview.indisponiveis} />
        ) : null}

        <View style={styles.totais}>
          {/* O "antes" só aparece quando existe diferença: repetir o mesmo
              número duas vezes ensina o cliente a ignorar o bloco. */}
          {preview.houveAjuste || preview.precoMudou ? (
            <View style={styles.totalLinha}>
              <Text style={styles.totalRotulo}>Total montado</Text>
              <Text style={[styles.totalValor, styles.textoRiscado]}>
                {BRL(preview.totalProgramado)}
              </Text>
            </View>
          ) : null}

          <View style={styles.totalLinha}>
            <Text style={styles.totalRotuloForte}>Total a pagar</Text>
            <Text style={styles.totalValorForte}>
              {BRL(preview.totalAjustado)}
            </Text>
          </View>
        </View>

        {preview.semItens ? (
          <View style={styles.blocoInviavel}>
            <Text style={styles.blocoInviavelTitulo}>
              Nenhum item disponível
            </Text>
            <Text style={styles.blocoInviavelTexto}>
              Nenhum item deste pedido está disponível para a sua loja no
              momento. Fale com seu vendedor.
            </Text>
          </View>
        ) : null}

        {bloqueado ? (
          <View style={styles.blocoInviavel}>
            <Text style={styles.blocoInviavelTitulo}>Regularize seu débito</Text>
            <Text style={styles.blocoInviavelTexto}>
              Você tem título vencido há mais de{' '}
              {preview.bloqueioFinanceiro?.diasDeTolerancia} dias. Regularize
              para pagar este pedido.
            </Text>
            {/* O bloqueio é TOTAL (RN-23 item 3) — não há "pagar mesmo assim".
                Sem um caminho pra regularizar, o cliente lê o aviso e não tem
                o que fazer com ele além de sair da tela. */}
            <TouchableOpacity
              style={styles.blocoInviavelBotao}
              onPress={() => navigate(AppRoutes.DEBTS)}>
              <Text style={styles.blocoInviavelBotaoTexto}>
                Ver débitos em aberto
              </Text>
            </TouchableOpacity>
          </View>
        ) : null}

        {podePagar ? (
          <TouchableOpacity style={styles.botaoPrimario} onPress={pagar}>
            {/* "Pagar", e não "Aceitar": não existe passo de aceite neste
                fluxo, e um botão de aceitar prometeria um segundo passo. */}
            <Text style={styles.botaoPrimarioTexto}>
              Pagar {BRL(preview.totalAjustado)}
            </Text>
          </TouchableOpacity>
        ) : null}

        <TouchableOpacity style={styles.botaoSecundario} onPress={goBack}>
          <Text style={styles.botaoSecundarioTexto}>Agora não</Text>
        </TouchableOpacity>
      </ScrollView>
    </View>
  );
};

export default AVistaOrderReview;
